import axios from 'axios';

/**
 * Admin Project Service
 * Handles all admin project-related API operations
 * Uses the global axios interceptors from AdminAuthService for authentication
 */
class AdminProjectService {
  /**
   * Get projects with pagination and filters
   */
  async getProjects(params = {}) {
    try {
      const response = await axios.get('/api/admin/projects', { params });
      return response.data;
    } catch (error) {
      console.error('Get projects error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get project details by ID
   */
  async getProjectById(projectId) {
    try {
      const response = await axios.get(`/api/admin/projects/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Get project by ID error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Approve a project
   */
  async approveProject(projectId, notes = '') {
    try {
      const response = await axios.put(`/api/admin/projects/${projectId}/approve`, { notes });
      return response.data;
    } catch (error) {
      console.error('Approve project error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Reject a project with required reason
   */
  async rejectProject(projectId, reason) {
    try {
      const response = await axios.put(`/api/admin/projects/${projectId}/reject`, { reason });
      return response.data;
    } catch (error) {
      console.error('Reject project error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get project action history
   */
  async getProjectHistory(projectId) {
    try {
      const response = await axios.get(`/api/admin/projects/${projectId}/history`);
      return response.data;
    } catch (error) {
      console.error('Get project history error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get project statistics for dashboard
   */
  async getProjectStats() {
    try {
      const response = await axios.get('/api/admin/projects/stats');
      return response.data;
    } catch (error) {
      console.error('Get project stats error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Bulk approve multiple projects
   */
  async bulkApproveProjects(projectIds) {
    try {
      const response = await axios.post('/api/admin/projects/bulk-approve', { projectIds });
      return response.data;
    } catch (error) {
      console.error('Bulk approve projects error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update project status manually
   */
  async updateProjectStatus(projectId, status, reason = null) {
    try {
      const response = await axios.put(`/api/admin/projects/${projectId}/status`, { status, reason });
      return response.data;
    } catch (error) {
      console.error('Update status error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { data } = error.response;
      return {
        message: data.error?.message || 'An error occurred',
        code: data.error?.code || 'API_ERROR',
        status: error.response.status,
        response: error.response,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        status: 0,
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        status: 0,
      };
    }
  }

  /**
   * Format project data for display
   */
  formatProjectForDisplay(project) {
    const currency = project.currency || 'PI';
    const symbol = currency === 'NGN' ? '₦' : 'π';

    return {
      ...project,
      formattedFundingGoal: project.funding_goal
        ? `${symbol} ${parseFloat(project.funding_goal).toLocaleString()}`
        : 'N/A',
      formattedCurrentFunding: project.current_funding
        ? `${symbol} ${parseFloat(project.current_funding).toLocaleString()}`
        : `${symbol} 0`,
      submittedDate: project.submitted_at
        ? new Date(project.submitted_at).toLocaleDateString()
        : 'N/A',
      createdDate: new Date(project.created_at).toLocaleDateString(),
      ownerName: project.owner?.pi_username || project.owner?.full_name || project.owner?.google_name || 'Unknown',
      statusBadgeColor: this.getStatusBadgeColor(project.status),
      fundingProgress: project.funding_goal
        ? Math.round((parseFloat(project.current_funding) / parseFloat(project.funding_goal)) * 100)
        : 0,
      currencySymbol: symbol
    };
  }

  /**
   * Get status badge color for UI
   */
  getStatusBadgeColor(status) {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-gray-100 text-gray-800',
      suspended: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      hidden: 'bg-red-50 text-red-700 border border-red-100'
    };
    return colors[status] || colors.draft;
  }

  /**
   * Get category display name
   */
  getCategoryDisplayName(category) {
    const categories = {
      technology: 'Technology',
      health: 'Health & Medical',
      education: 'Education',
      environment: 'Environment',
      social: 'Social Impact',
      arts: 'Arts & Culture',
      business: 'Business',
      other: 'Other'
    };
    return categories[category] || category;
  }

  /**
   * Validate rejection reason
   */
  validateRejectionReason(reason) {
    if (!reason || reason.trim().length < 10) {
      throw new Error('Rejection reason must be at least 10 characters long');
    }
    if (reason.trim().length > 1000) {
      throw new Error('Rejection reason must be less than 1000 characters');
    }
    return reason.trim();
  }

  /**
   * Get time ago string for dates
   */
  getTimeAgo(date) {
    const now = new Date();
    const diffInSeconds = Math.floor((now - new Date(date)) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return new Date(date).toLocaleDateString();
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount, currency = 'PI') {
    const symbol = currency === 'NGN' ? '₦' : 'π';
    if (!amount) return `${symbol} 0`;

    return `${symbol} ${parseFloat(amount).toLocaleString()}`;
  }
}

export default new AdminProjectService();
